import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import api from "@/lib/api";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  PenTool,
  Trash2,
  Copy,
  MoreVertical,
  Clock,
  User,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/lib/utils";
import { CategoryManagerButton } from "@/components/CategoryManagerButton";

export default function DiagramListPage() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { canManage } = useAuth();
  const [diagrams, setDiagrams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadDiagrams();
  }, [workspaceId]);

  const loadDiagrams = async () => {
    try {
      const response = await api.get(`/workspaces/${workspaceId}/diagrams`);
      setDiagrams(response.data);
    } catch (error) {
      console.error("Failed to load diagrams:", error);
      toast.error("Erreur lors du chargement des diagrammes");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);

    try {
      await api.delete(`/workspaces/${workspaceId}/diagrams/${deleteId}`);
      setDiagrams(diagrams.filter(d => d.id !== deleteId));
      toast.success("Diagramme supprimé");
    } catch (error) {
      console.error("Failed to delete diagram:", error);
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  const handleDuplicate = async (diagram) => {
    try {
      const response = await api.post(`/workspaces/${workspaceId}/diagrams`, {
        name: `${diagram.name} (copie)`,
        elements: diagram.elements,
        workspace_id: workspaceId,
      });
      setDiagrams([response.data, ...diagrams]);
      toast.success("Diagramme dupliqué");
    } catch (error) {
      console.error("Failed to duplicate diagram:", error);
      toast.error("Erreur lors de la duplication");
    }
  };

  if (loading) {
    return (
      <WorkspaceLayout>
        <div className="page-container">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-48 skeleton rounded-lg" />
            ))}
          </div>
        </div>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout>
      <div className="page-container animate-fade-in">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <PenTool className="h-6 w-6 text-primary" />
              <h1 className="text-3xl font-semibold tracking-tight">LearSpace Draw</h1>
              <Badge className="bg-gradient-to-r from-purple-500 to-pink-500">Nouveau</Badge>
            </div>
            <p className="mt-1 text-muted-foreground">
              Créez des diagrammes DevOps professionnels avec 150+ icônes
            </p>
          </div>
          <div className="flex gap-2">
            <CategoryManagerButton />
            <Link to={`/workspace/${workspaceId}/draw/new`}>
              <Button className="gap-2" data-testid="create-diagram-btn">
                <Plus className="h-4 w-4" />
                Nouveau diagramme
              </Button>
            </Link>
          </div>
        </div>

        {/* Features highlight */}
        <div className="grid gap-4 mt-6 md:grid-cols-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 border-blue-200 dark:border-blue-800">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
                <Sparkles className="h-5 w-5" />
                <span className="font-medium">150+ Icônes DevOps</span>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950 dark:to-green-900 border-green-200 dark:border-green-800">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                <PenTool className="h-5 w-5" />
                <span className="font-medium">Dessin collaboratif</span>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950 dark:to-purple-900 border-purple-200 dark:border-purple-800">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-purple-700 dark:text-purple-300">
                <Copy className="h-5 w-5" />
                <span className="font-medium">Export SVG/PNG</span>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-950 dark:to-orange-900 border-orange-200 dark:border-orange-800">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-orange-700 dark:text-orange-300">
                <Clock className="h-5 w-5" />
                <span className="font-medium">Sauvegarde auto</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Diagrams Grid */}
        {diagrams.length === 0 ? (
          <Card className="mt-8 border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <PenTool className="h-10 w-10 text-primary" />
              </div>
              <h3 className="font-semibold text-lg">Aucun diagramme</h3>
              <p className="mt-2 text-sm text-muted-foreground text-center max-w-md">
                Créez votre premier diagramme pour documenter vos architectures DevOps,
                pipelines CI/CD, et infrastructures Kubernetes.
              </p>
              <Link to={`/workspace/${workspaceId}/draw/new`}>
                <Button className="mt-6 gap-2">
                  <Plus className="h-4 w-4" />
                  Créer mon premier diagramme
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 mt-8 md:grid-cols-2 lg:grid-cols-3">
            {diagrams.map((diagram, index) => (
              <Card
                key={diagram.id}
                className="group hover:shadow-lg transition-all cursor-pointer"
                data-testid={`diagram-card-${index}`}
              >
                <Link to={`/workspace/${workspaceId}/draw/${diagram.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <CardTitle className="truncate text-lg group-hover:text-primary transition-colors">
                          {diagram.name}
                        </CardTitle>
                        <CardDescription className="mt-1 flex items-center gap-2">
                          <User className="h-3 w-3" />
                          {diagram.created_by_name}
                        </CardDescription>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.preventDefault()}>
                          <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => {
                            e.preventDefault();
                            handleDuplicate(diagram);
                          }}>
                            <Copy className="h-4 w-4 mr-2" />
                            Dupliquer
                          </DropdownMenuItem>
                          {canManage && (
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={(e) => {
                                e.preventDefault();
                                setDeleteId(diagram.id);
                              }}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Supprimer
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Preview placeholder */}
                    <div className="aspect-video bg-muted rounded-md flex items-center justify-center mb-3">
                      <PenTool className="h-8 w-8 text-muted-foreground/50" />
                    </div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDate(diagram.updated_at)}
                      </span>
                      <Badge variant="secondary">
                        {diagram.elements?.length || 0} éléments
                      </Badge>
                    </div>
                  </CardContent>
                </Link>
              </Card>
            ))}
          </div>
        )}

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer ce diagramme ?</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action est irréversible. Le diagramme sera définitivement supprimé.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} disabled={deleting}>
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </WorkspaceLayout>
  );
}
